import * as React from 'react';

type OverlayState = { active: boolean; fading: boolean };
type OverlayCtx = OverlayState & {
  show: () => void;
  hideWithFade: (durationMs?: number) => void;
};

const Ctx = React.createContext<OverlayCtx | undefined>(undefined);

export const RouteLoadingOverlayProvider = ({ children }: { children: React.ReactNode }) => {
  const [{ active, fading }, set] = React.useState<OverlayState>({ active: false, fading: false });
  const timerRef = React.useRef<number | null>(null);

  const clearTimer = () => {
    if (timerRef.current !== null) {
      window.clearTimeout(timerRef.current);
      timerRef.current = null;
    }
  };

  const show = React.useCallback(() => {
    clearTimer();
    set(s => s.active && !s.fading ? s : { active: true, fading: false });
  }, []);

  const hideWithFade = React.useCallback((durationMs = 250) => {
    set(s => s.active ? { active: true, fading: true } : s);
    clearTimer();
    timerRef.current = window.setTimeout(() => {
      set(s => s.fading ? { active: false, fading: false } : s);
      timerRef.current = null;
    }, durationMs);
  }, []);

  React.useEffect(() => () => clearTimer(), []);

  const value = React.useMemo<OverlayCtx>(
    () => ({ active, fading, show, hideWithFade }),
    [active, fading, show, hideWithFade]
  );

  return <Ctx.Provider value={value}>{children}</Ctx.Provider>;
};

const useRouteOverlay = () => {
  const ctx = React.useContext(Ctx);

  if (!ctx) throw new Error('useRouteOverlay must be used within RouteLoadingOverlayProvider');

  return ctx;
};

const RouteLoadingOverlay = () => {
  const { active, fading } = useRouteOverlay();
  if (!active) return null;
  return (
    <div
      className={`route-loading-overlay${fading ? ' route-loading-overlay--fade-out' : ''}`}
      aria-live="polite"
      aria-busy={!fading}
    >
      <div className="route-loading-spinner" />
    </div>
  );
};

export default RouteLoadingOverlay;

export const RouteLoadingFallback = () => {
  const { show, hideWithFade } = useRouteOverlay();

  React.useLayoutEffect(() => {
    show();
    return () => hideWithFade();
  }, [show, hideWithFade]);

  return null;
};
